/**
 * One notice, in full.
 *
 * The lists carry headlines. This is where the words are, for the same reason
 * a ticket row links to a ticket: a notice worth reading is worth a page, and
 * a landing page that unrolls every notice in the group is not a landing page.
 *
 * A notice with no body never links here — most alerts are a single sentence
 * and the headline is the whole of it. So this page always has something to
 * show, and its empty state exists only for the case where a notice is
 * withdrawn between the list rendering and the click landing.
 */

import { Typography } from "@norican/react";
import { useParams } from "react-router";

import {
  AppBreadcrumbs,
  ErrorState,
  LoadingState,
  Markdown,
  PageContainer,
} from "@/components/common";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";

import { useMessage } from "./api";
import { builtinContent } from "./builtin-content";
import { HelpGuide } from "./help-guide";
import { useMessageKindLabel } from "./message-kinds";

/** The built-in "How to use" notice, which renders as a guide, not Markdown. */
const HELP_EXTERNAL_ID = "builtin:help";

export function MessageDetailPage() {
  const { messageId } = useParams<{ messageId: string }>();
  const { t } = useT("messages");
  const { t: ta } = useT("assistant");
  const { t: tc } = useT();
  const kindLabel = useMessageKindLabel();
  const message = useMessage(messageId);

  const builtin = builtinContent(message.data?.external_id, ta);
  const title = builtin?.title ?? message.data?.title ?? "";
  const body = builtin?.body ?? message.data?.body_markdown ?? "";

  useDocumentTitle(title === "" ? t("title") : title);

  if (message.isPending) {
    return <LoadingState label={tc("state.loading")} />;
  }
  if (message.isError || message.data === undefined) {
    return (
      <PageContainer title={t("title")}>
        <ErrorState error={message.error} onRetry={() => void message.refetch()} />
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={
        <Typography as="span" variant="display-md" className="text-3xl sm:text-4xl">
          {title}
        </Typography>
      }
      subtitle={kindLabel(message.data.kind)}
      breadcrumbs={
        <AppBreadcrumbs items={[{ label: t("title"), to: "/messages" }, { label: title }]} />
      }
    >
      <div className="max-w-[var(--norican-size-container-md)]">
        {message.data.external_id === HELP_EXTERNAL_ID ? (
          <HelpGuide />
        ) : body.trim() === "" ? (
          <Typography as="p" variant="body" tone="muted">
            {t("empty.body")}
          </Typography>
        ) : (
          <Markdown>{body}</Markdown>
        )}
      </div>
    </PageContainer>
  );
}
